import React from 'react'
import { FastField, Field } from 'formik'
import uniqBy from 'lodash/uniqBy'

import { Place, PlaceCategory } from '../../types/utils'
import { Customer } from '../../types/customer'
import placeService from '../../services/placeService'
import { graphQLClient } from '../../services/authService'
import customerQueries from '../../services/graphql/customers/queries'
import { useUser } from '../../hooks/useUser'

import FormikMultipleCheckbox from '../FormikMultipleCheckbox'
import FormikAsyncSelect from '../FormikAsyncSelect'
import FormikRange from '../FormikRange'

type LocationFiltersFormProps = {
  isSubmitting: boolean
}

const RATING = ['Positive', 'Negative', 'Neutral'].map((item) => ({ value: item.toLocaleUpperCase(), label: item }))
const STATUS = ['Claimed', 'Unclaimed', 'Pending'].map((item) => ({ value: item.toLocaleUpperCase(), label: item }))

const LocationFiltersForm: React.FC<LocationFiltersFormProps> = (props) => {
  const user = useUser()
  const placeOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    return await placeService.getAll({ where: { name_contains: inputValue } })
  }

  const areaOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    const allPlace = await placeService.getAll({ where: { location: { city_contains: inputValue } } })
    return uniqBy(allPlace, 'location.city')
  }

  const customerOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    const response = await graphQLClient.request(customerQueries.getAll, {
      where: { name_contains: inputValue },
    })
    return response.customers
  }

  const categoryOptions = async (inputValue: string) => {
    if (inputValue === '') return []
    return await placeService.getCategories(inputValue)
  }

  return (
    <div className="mb-6 space-y-6 px-4 sm:px-6">
      <fieldset className="mt-4">
        <p className="mb-4 font-medium text-gray-700">Rating</p>
        <FastField name="rating" component={FormikMultipleCheckbox} items={RATING} />
      </fieldset>
      {user.data?.type === 'BUZZL' ? (
        <fieldset>
          <p className="mb-4 font-medium text-gray-700">Status</p>
          <FastField name="status" component={FormikMultipleCheckbox} items={STATUS} />
        </fieldset>
      ) : null}
      <Field
        name="categories"
        component={FormikAsyncSelect}
        label="Category"
        isMulti
        loadOptions={categoryOptions}
        getOptionLabel={(item: PlaceCategory) => item.name}
        getOptionValue={(item: PlaceCategory) => item.id}
        placeholder="Type Category"
      />
      <Field
        name="locations"
        component={FormikAsyncSelect}
        label="Location"
        isMulti
        loadOptions={placeOptions}
        getOptionLabel={(item: Place) => item.name}
        getOptionValue={(item: Place) => item.id}
        placeholder="Type Location Name"
      />
      <Field
        name="addresses"
        component={FormikAsyncSelect}
        label="Address"
        isMulti
        loadOptions={areaOptions}
        getOptionLabel={(item: Place) => item.address}
        getOptionValue={(item: Place) => item.address}
        placeholder="Type Address"
      />
      {user.data?.type === 'BUZZL' ? (
        <Field
          name="customers"
          component={FormikAsyncSelect}
          label="Customer"
          isMulti
          loadOptions={customerOptions}
          getOptionLabel={(item: Customer) => item.name}
          getOptionValue={(item: Customer) => item.id}
          placeholder="Select Customer"
        />
      ) : null}
      <FastField name="nbExperiences" label="Number of Experiences" component={FormikRange} min={0} max={1000} />
    </div>
  )
}

export default LocationFiltersForm
